import { z } from "zod";

const AUTO_GENERATED_PASSWORD_REGEX = /^[A-Za-z0-9_-]{16}$/;
const STRONG_PASSWORD_SPECIAL_CHARS_REGEX = /[!@#$%^&*(),.?":{}|<>]/;

export const loginSchema = z.object({
  identifier: z.string().min(1, "identifier.error.required"),

  password: z
    .string()
    .min(1, "password.error.required")
    .superRefine((value, ctx) => {
      const isAutoGenerated = AUTO_GENERATED_PASSWORD_REGEX.test(value);
      if (isAutoGenerated) return;

      if (value.length < 8) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "password.error.minLength" });
      }
      if (!/[A-Z]/.test(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "password.error.uppercase" });
      }
      if (!/[0-9]/.test(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "password.error.digit" });
      }
      if (!STRONG_PASSWORD_SPECIAL_CHARS_REGEX.test(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "password.error.specialChar" });
      }
    }),
});

export const changePasswordSchema = z
  .object({
    old_password: z.string().optional(),
    new_password: z
      .string()
      .min(1, "password.error.required")
      .regex(/^.{8,}$/, "password.error.minLength")
      .regex(/[A-Z]/, "password.error.uppercase")
      .regex(/[0-9]/, "password.error.digit")
      .regex(STRONG_PASSWORD_SPECIAL_CHARS_REGEX, "password.error.specialChar"),
    confirm_password: z.string().min(1, "password.error.required"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "password.error.mismatch",
    path: ["confirm_password"],
  });

export const forgotPasswordSchema = z.object({
  email: z.string().min(1, "email.error.required").email("email.error.invalid"),
});

export const resetPasswordSchema = z
  .object({
    new_password: z
      .string()
      .min(1, "password.error.required")
      .regex(/^.{8,}$/, "password.error.minLength")
      .regex(/[A-Z]/, "password.error.uppercase")
      .regex(/[0-9]/, "password.error.digit")
      .regex(STRONG_PASSWORD_SPECIAL_CHARS_REGEX, "password.error.specialChar"),
    confirm_password: z.string().min(1, "password.error.required"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "password.error.mismatch",
    path: ["confirm_password"],
  });

export type LoginFormValues = z.infer<typeof loginSchema>;
export type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>;
export type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;
